import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Truck, Shield, Clock, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { useAuth } from '../contexts/AuthContext';
import { usePincode } from '../contexts/PincodeContext';
import { t, getCategoryName, getBannerTitle } from '../lib/translations';
import ProductCard from '../components/ProductCard';
import CategoryCarousel from '../components/CategoryCarousel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { language } = useAuth();
  const { currentPincode } = usePincode();
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bannersRes, categoriesRes, productsRes] = await Promise.all([
          axios.get(`${API}/banners`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?pincode=${currentPincode}&page_size=8`)
        ]);
        setBanners(bannersRes.data);
        const cats = categoriesRes.data;
        setCategories(cats);
        setProducts(productsRes.data.products || productsRes.data);

        // Fetch products for first 5 categories
        const top5Categories = cats.slice(0, 5);
        const categoryProductsData = {};
        
        await Promise.all(
          top5Categories.map(async (cat) => {
            try {
              const res = await axios.get(`${API}/products?pincode=${currentPincode}&category_id=${cat.id}&page_size=4`);
              categoryProductsData[cat.id] = res.data.products || res.data;
            } catch (err) {
              categoryProductsData[cat.id] = [];
            }
          })
        );
        
        setCategoryProducts(categoryProductsData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    fetchData();
  }, [currentPincode]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Banner Carousel */}
      {banners.length > 0 && (
        <section className="bg-gradient-to-br from-green-50 to-white">
          <div className="container-app py-6">
            <Carousel className="w-full" opts={{ loop: true }}>
              <CarouselContent>
                {banners.map((banner) => (
                  <CarouselItem key={banner.id}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[21/9] md:aspect-[3/1]">
                      <img
                        src={banner.image}
                        alt={getBannerTitle(banner, language)}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
                        <div className="p-6 md:p-12 max-w-lg">
                          <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
                            {getBannerTitle(banner, language)}
                          </h2>
                          <Link to="/products">
                            <Button className="bg-green-600 hover:bg-green-700" data-testid="shop-now-btn">
                              {t('shopNow', language)}
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-4" />
              <CarouselNext className="right-4" />
            </Carousel>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="border-y border-slate-100 bg-white">
        <div className="container-app py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900">
                  {language === 'hi' ? 'मुफ्त डिलीवरी' : 'Free Delivery'}
                </p>
                <p className="text-xs text-slate-500">
                  {language === 'hi' ? '₹500+ पर' : 'On ₹500+'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900">
                  {language === 'hi' ? 'गुणवत्ता आश्वासन' : 'Quality Assured'}
                </p>
                <p className="text-xs text-slate-500">
                  {language === 'hi' ? '100% ताज़ा' : '100% Fresh'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900">
                  {language === 'hi' ? 'तेज़ डिलीवरी' : 'Fast Delivery'}
                </p>
                <p className="text-xs text-slate-500">
                  {language === 'hi' ? 'उसी दिन' : 'Same Day'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-slate-900">
                  {language === 'hi' ? 'सहायता' : 'Support'}
                </p>
                <p className="text-xs text-slate-500">24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category Carousel */}
      <section className="py-8 md:py-12">
        <div className="container-app">
          <CategoryCarousel
            categories={categories}
            language={language}
            title={t('shopByCategory', language)}
            showViewAll={true}
            viewAllLink="/products"
          />
        </div>
      </section>

      {/* Category-wise Product Sections */}
      {categories.slice(0, 5).map((category) => {
        const catProducts = categoryProducts[category.id] || [];
        if (catProducts.length === 0) return null;
        
        return (
          <section key={category.id} className="py-8 md:py-12 bg-slate-50">
            <div className="container-app">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {getCategoryName(category, language)}
                </h2>
                <Link
                  to={`/products?category=${category.id}`}
                  className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center"
                >
                  {t('viewAll', language)}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
              <div className="product-grid">
                {catProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Featured Products */}
      <section className="py-8 md:py-12 bg-slate-50">
        <div className="container-app">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              {t('featuredProducts', language)}
            </h2>
            <Link to="/products" className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center">
              {t('viewAll', language)}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          
          <div className="product-grid" data-testid="product-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-green-600">
        <div className="container-app text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {language === 'hi' ? 'आज ही अपना पहला ऑर्डर दें!' : 'Place Your First Order Today!'}
          </h2>
          <p className="text-green-100 mb-6 max-w-lg mx-auto">
            {language === 'hi' 
              ? 'ताज़ी सब्जियां, किराना और रोज़मर्रा की ज़रूरतें - सब कुछ आपके दरवाज़े पर।'
              : 'Fresh vegetables, groceries and daily essentials - everything delivered to your doorstep.'}
          </p>
          <Link to="/products">
            <Button className="bg-white text-green-600 hover:bg-green-50" data-testid="explore-products-btn">
              {language === 'hi' ? 'उत्पाद देखें' : 'Explore Products'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
